import {
  Box,
  Button,
  InputAdornment,
  TextField,
  Typography,
  useMediaQuery,
} from '@material-ui/core';
import { ToggleButton, ToggleButtonGroup } from '@material-ui/lab';
import Search from '@material-ui/icons/Search';
import AddIcon from '@material-ui/icons/Add';
import { useStyles } from '../../../styles';
import { AdminActionHint } from '../../../common/AdminActionHint';
import '../../../styles/projectHeader.css';

export type ViewMode = 'grid' | 'list';

interface ProjectsHeaderProps {
  projectCount: number;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  onAddProject: () => void;
  isToolAdmin: boolean;
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
}

export const ProjectsHeader = ({
  projectCount,
  searchQuery,
  onSearchChange,
  onAddProject,
  isToolAdmin,
  viewMode,
  onViewModeChange,
}: ProjectsHeaderProps) => {
  const classes = useStyles();
  const isMobile = useMediaQuery('(max-width: 1023px)');

  return (
    <Box
      component="header"
      className={`projects-header ${classes.projectsHeaderRoot}${
        isMobile ? ' projects-header-mobile' : ''
      }${!isToolAdmin ? ' projects-header-admin-restricted' : ''}`}
    >
      <Box className="projects-header-left">
        <Typography
          variant="h5"
          align={isMobile ? 'left' : undefined}
          className={`${classes.projectsHeaderTitle} projects-header-title${
            isMobile ? ' projects-header-title-mobile' : ''
          }`}
        >
          {`Projects (${projectCount})`}
        </Typography>

        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(_e, newView) => {
            if (newView !== null) {
              onViewModeChange(newView as ViewMode);
            }
          }}
          aria-label="Switch between grid and list view"
          className="projects-header-toggle"
          style={{ display: isMobile ? 'none' : undefined }}
        >
          <ToggleButton value="grid" className="projects-header-toggle-button">
            Grid
          </ToggleButton>
          <ToggleButton value="list" className="projects-header-toggle-button">
            List
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Box
        className={`projects-header-controls${
          isMobile ? ' projects-header-controls-mobile' : ''
        }${!isToolAdmin ? ' projects-header-controls-admin-restricted' : ''}`}
      >
        <Box className="projects-header-fields">
          <TextField
            variant="outlined"
            size="small"
            placeholder="Search projects"
            value={searchQuery}
            onChange={e => onSearchChange(e.target.value)}
            className="projects-header-search"
            inputProps={{ 'aria-label': 'Search projects' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          {/* typography resets + height + shadow in projectHeader.css */}
          <Button
            variant="contained"
            color="primary"
            className="projects-header-add-btn"
            onClick={onAddProject}
            aria-label="Add a new project"
            disabled={!isToolAdmin}
            startIcon={<AddIcon />}
          >
            Add new Project
          </Button>
        </Box>
        <AdminActionHint
          isToolAdmin={isToolAdmin}
          className="projects-header-permission-hint"
        />
      </Box>
    </Box>
  );
};
